import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Clock, Calendar, Printer, RefreshCw, BookOpen, Users, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getUser } from '@/lib/auth';

const DAYS = [
  { id: 'monday', label: 'Segunda', short: 'SEG', color: 'bg-amber-500' },
  { id: 'tuesday', label: 'Terça', short: 'TER', color: 'bg-rose-500' },
  { id: 'wednesday', label: 'Quarta', short: 'QUA', color: 'bg-emerald-500' },
  { id: 'thursday', label: 'Quinta', short: 'QUI', color: 'bg-orange-500' },
  { id: 'friday', label: 'Sexta', short: 'SEX', color: 'bg-cyan-500' }
];

const DEFAULT_TIME_SLOTS = [
  { id: '07:05', label: '07:05 - 07:50', turno: 'manha' },
  { id: '07:50', label: '07:50 - 08:35', turno: 'manha' },
  { id: '08:35', label: '08:35 - 09:20', turno: 'manha' },
  { id: '09:40', label: '09:40 - 10:25', turno: 'manha' },
  { id: '10:25', label: '10:25 - 11:10', turno: 'manha' },
  { id: '11:10', label: '11:10 - 11:55', turno: 'manha' },
  { id: '11:55', label: '11:55 - 12:40', turno: 'manha' },
  { id: '13:05', label: '13:05 - 13:50', turno: 'tarde' },
  { id: '13:50', label: '13:50 - 14:35', turno: 'tarde' },
  { id: '14:35', label: '14:35 - 15:20', turno: 'tarde' },
  { id: '15:40', label: '15:40 - 16:25', turno: 'tarde' },
  { id: '16:25', label: '16:25 - 17:10', turno: 'tarde' },
  { id: '17:10', label: '17:10 - 17:55', turno: 'tarde' },
];

// Cores para turmas
const getTurmaColor = (turmaName) => {
  if (!turmaName) return 'bg-slate-100 text-slate-700';
  
  const colors = [
    'bg-blue-100 text-blue-800 border-blue-200',
    'bg-purple-100 text-purple-800 border-purple-200',
    'bg-green-100 text-green-800 border-green-200',
    'bg-amber-100 text-amber-800 border-amber-200',
    'bg-pink-100 text-pink-800 border-pink-200',
    'bg-indigo-100 text-indigo-800 border-indigo-200',
    'bg-teal-100 text-teal-800 border-teal-200',
    'bg-orange-100 text-orange-800 border-orange-200',
  ];
  
  const hash = turmaName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export default function TeacherScheduleView({ 
  employeeId = null, // Se null, usa o usuário logado
  onClose = null,
  showHeader = true,
  className = ''
}) {
  const user = getUser();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTurno, setSelectedTurno] = useState('todos');
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    loadSchedules();
  }, [employeeId]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      // Determinar o ID do funcionário
      let empId = employeeId;
      
      if (!empId && user) {
        // Buscar employee pelo email do usuário
        const empRes = await api.get('/employees');
        const emp = empRes.data?.find(e => e.email === user.email);
        if (emp) {
          empId = emp.id;
          setEmployee(emp);
        }
      }

      if (!empId) {
        toast.error('Funcionário não encontrado');
        setLoading(false);
        return;
      }

      // Carregar horários do professor
      const res = await api.get(`/class-schedules?employee_id=${empId}`);
      setSchedules(res.data || []);
      
      // Carregar dados do funcionário se não temos
      if (!employee && empId) {
        const empRes = await api.get(`/employees/${empId}`);
        setEmployee(empRes.data);
      }
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
      toast.error('Erro ao carregar horários');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar horários por turno
  const filteredSlots = selectedTurno === 'todos' 
    ? DEFAULT_TIME_SLOTS 
    : DEFAULT_TIME_SLOTS.filter(s => s.turno === selectedTurno);

  // Identificar turnos do professor
  const professorTurnos = new Set();
  schedules.forEach(s => {
    const hour = parseInt(s.time_slot?.split(':')[0] || '0');
    professorTurnos.add(hour < 12 ? 'manha' : 'tarde');
  });

  // Obter aula para slot
  const getScheduleForSlot = (day, timeSlot) => {
    return schedules.find(s => s.day === day && s.time_slot === timeSlot);
  };

  // Estatísticas
  const stats = {
    total: schedules.length,
    turmas: [...new Set(schedules.map(s => s.class_group_name || s.class_name))].length,
    disciplinas: [...new Set(schedules.map(s => s.subject_name))].length
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-purple-600" />
              Meus Horários
            </h2>
            {employee && (
              <p className="text-slate-500 text-sm mt-1">
                {employee.full_name || employee.name}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Select value={selectedTurno} onValueChange={setSelectedTurno}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Turno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="manha">Manhã</SelectItem>
                <SelectItem value="tarde">Tarde</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" />
              Imprimir
            </Button>

            <Button variant="outline" size="sm" onClick={loadSchedules}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Alerta se professor leciona em dois turnos */}
      {professorTurnos.size > 1 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Você leciona nos turnos da <strong>manhã</strong> e <strong>tarde</strong>
        </div>
      )}

      {/* Estatísticas */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary" className="bg-purple-100 text-purple-800">
          <BookOpen className="w-3 h-3 mr-1" />
          {stats.total} aulas/semana
        </Badge>
        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
          <Users className="w-3 h-3 mr-1" />
          {stats.turmas} turma(s)
        </Badge>
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          <MapPin className="w-3 h-3 mr-1" />
          {stats.disciplinas} disciplina(s)
        </Badge>
      </div>

      {/* Grade de Horários */}
      {schedules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Nenhum horário cadastrado</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-gradient-to-r from-purple-700 to-purple-800 text-white p-3 text-sm font-semibold min-w-[80px]">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-4 h-4" />
                      </div>
                    </th>
                    {DAYS.map(day => (
                      <th 
                        key={day.id} 
                        className={cn("p-3 text-sm font-semibold text-white min-w-[110px]", day.color)}
                      >
                        <div className="hidden sm:block">{day.label}</div>
                        <div className="sm:hidden">{day.short}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSlots.map((slot, index) => (
                    <tr key={slot.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="sticky left-0 z-10 bg-inherit border p-2 text-center">
                        <div className="text-xs font-medium text-slate-600">{slot.label}</div>
                      </td>
                      {DAYS.map(day => {
                        const schedule = getScheduleForSlot(day.id, slot.id);
                        const turmaColor = getTurmaColor(schedule?.class_group_name || schedule?.class_name);
                        
                        return (
                          <td 
                            key={`${day.id}-${slot.id}`} 
                            className="border p-0.5 text-center min-w-[110px] h-[55px]"
                          >
                            {schedule ? (
                              <div className={cn(
                                "h-full w-full rounded-md p-1.5 flex flex-col justify-center border",
                                turmaColor
                              )}>
                                <p className="font-semibold text-[10px] leading-tight truncate">
                                  {schedule.class_group_name || schedule.class_name || 'Turma'}
                                </p>
                                <p className="text-[9px] opacity-75 truncate">
                                  {schedule.subject_name}
                                </p>
                              </div>
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-slate-300">
                                -
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
